import React from 'react';
import { Link } from 'react-router-dom';
import { Field, reduxForm } from 'redux-form';
import { Alert, Form, Button, Row, Col, ControlLabel, FormGroup, FormControl, Panel, Label, Table } from 'react-bootstrap';
import { FormActions, Static, FormFileUpload } from '../../common/components/formControls';
import { withTranslation } from 'react-i18next';
import { ConfirmationButton } from '../../common/components/formControls';
import ui from '../../common/services/ui';
import BackButton from '../../common/components/BackButton';
import { mapStatusToLabel } from '../paymentHistory/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LabelWithTooltip from '../../common/components/LabelWithTooltip';
import { faCheck, faTimes, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { faEnvelope } from '@fortawesome/free-regular-svg-icons';
import InstalmentList from '../paymentHistory/InstalmentList';
import moment from 'moment';

const SettingsForm = ({ error, handleSubmit, pristine, submitting, isReadonly, submitSucceeded, initialValues, invoiceFile, canMarkAsPaid, canCancel, markAsPaid, markAsCancelled,
	canRefund, refundIndividualConsultation, canUploadInvoice, canSendInvoice, sendInvoice, canRetryInstalment, retryInstalment, onRefresh, markInstalmentAsPaid, canMarkInstalmentAsPaid, t }) => {

	const total = initialValues && `${ui.formatAmount(initialValues.total)}${initialValues.refundedTotal ? ", " + t('PaymentHistory_TotalRefunded') + " " + ui.formatAmount(initialValues.refundedTotal) : ""}`;
	const parentId = initialValues && initialValues.parent.id;
	const childId = initialValues && initialValues.child.id;
	const paymentOption = initialValues && initialValues.paymentOption && `(${initialValues.paymentOption})`;
	const paymentMethodName = (initialValues && initialValues.paymentMethod && initialValues.paymentMethod.name) || '';
	const isVoucherPayment = paymentMethodName === 'Voucher';
	const isGoCardlessPayment = paymentMethodName === 'GoCardless';
	const isInactive = initialValues && initialValues.isInactive;

	return (
		<Form horizontal onSubmit={handleSubmit}>
			{error && <Alert bsStyle='danger'>{error}</Alert>}
			{submitSucceeded && pristine && <Alert bsStyle='success'>{t('Common_UpdateSuccess')}</Alert>}
			{initialValues.isPaymentError && !isInactive && <Alert bsStyle='danger'>{initialValues.cancelationReason}</Alert>}

			<Row>
				<Col sm={6}>
					<Panel>
						<Panel.Heading>{t('PaymentHistory_Order')}</Panel.Heading>
						<Panel.Body>
							<Field name='id' component={Static} labelSize={4} horizontal label={t('PaymentHistory_Id')} />
							<Field name='created' component={Static} labelSize={4} horizontal formatType='dateTime' label={t('PaymentHistory_CreatedDate')} />
							<Field name='status' component={Static} labelSize={4} horizontal label={t('PaymentHistory_Status')} valueWrapper={value => <span>
								<Label bsStyle={mapStatusToLabel(value.id)}>{t(`PaymentStatusType_${value.name}`)}</Label>
								{isInactive && <span> <Label bsStyle='inverse'>{t('PaymentStatus_Inactive')}</Label></span>}
							</span>} />
							<Field name='paymentDate' component={Static} labelSize={4} formatType='dateTime' horizontal label={t('PaymentHistory_PaymentDate')} />
						</Panel.Body>
					</Panel>
				</Col>
				<Col sm={6}>
					<Panel>
						<Panel.Heading>{t('PaymentHistory_Parent')}</Panel.Heading>
						<Panel.Body>
							<Field name='parent.fullname' component={Static} labelSize={4} horizontal label={t('PaymentHistory_Parent')} valueWrapper={value => <Link to={`/parents/${parentId}`}>{value}</Link>} />
							<Field name='parent.email' component={Static} labelSize={4} horizontal label={t('Parent_Email')} />
							<Field name='parent.phoneNumber' component={Static} labelSize={4} horizontal label={t('PaymentHistory_PhoneNumber')} />
							<Field name='child.fullname' component={Static} labelSize={4} horizontal label={t('PaymentHistory_Child')} valueWrapper={value => <Link to={`/parents/${parentId}/children/${childId}`}>{value}</Link>} />
						</Panel.Body>
					</Panel>
				</Col>
			</Row>

			<Row>
				<Col sm={6}>
					<Panel>
						<Panel.Heading>{t('PaymentHistory_Enrolment')}</Panel.Heading>
						<Panel.Body>
							<Field name='center.name' component={Static} labelSize={4} horizontal label={t('PaymentHistory_Center')} />
							<Field name='type.name' component={Static} labelSize={4} horizontal label={t('IndividualConsultation_Type')} valueWrapper={value => <span>
								{t(`IndividualConsultationType_${value}`)}
							</span>} />
							<Field name='consultationDate' component={Static} labelSize={4} horizontal formatType={'dateTime'} label={t('IndividualConsultation_ConsultationDate')} />
						</Panel.Body>
					</Panel>
				</Col>
				<Col sm={6}>
					<Panel>
						<Panel.Heading>{t('PaymentHistory_PaymentMethod')}</Panel.Heading>
						<Panel.Body>
							<Field name='paymentMethod.name' component={Static} labelSize={4} horizontal label={t('PaymentHistory_PaymentMethod')} valueWrapper={value => `${value} ${paymentOption || ''}`} />
							{initialValues.paymentCode && <Field name='paymentCode' component={Static} labelSize={4} horizontal label={t('PaymentHistory_PaymnetCode')} />}
							<Field name='transaction' component={Static} labelSize={4} horizontal label={t('PaymentHistory_Transaction')} />
							{isVoucherPayment && <Field name='voucherSchemeName' component={Static} labelSize={4} horizontal label={t('PaymentHistory_VoucherSchemeName')} />}
							{isVoucherPayment && <Field name='voucherSchemeReference' component={Static} labelSize={4} horizontal label={t('PaymentHistory_VoucherSchemeReference')} />}
							{isGoCardlessPayment && <Field name='authorizationToken' component={Static} labelSize={4} horizontal label={t('PaymentHistory_Mandate')} />}
							{initialValues.isInstalmentPayment && initialValues.subscriptionTransaction && < Field name='subscriptionTransaction' component={Static} labelSize={4} horizontal label={t(`PaymentHistory_${paymentMethodName}_Subscription`)} />}
						</Panel.Body>
					</Panel>
				</Col>
			</Row>

			<Row>
				<Col sm={6}>
					<Panel>
						<Panel.Heading>{t('PaymentHistory_Amounts')}</Panel.Heading>
						<Panel.Body>
							<Table>
								<thead>
									<tr>
										<th>{t('PaymentHistory_Price')}</th>
										<th>{t('PaymentHistory_NumberOfHours')}</th>
										<th>{t('PaymentHistory_Subtotal')}</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td>{ui.formatAmount(initialValues.price)}</td>
										<td>{initialValues.quantity}</td>
										<td>{ui.formatAmount(initialValues.subtotal)}</td>
									</tr>
								</tbody>
								<tfoot>
									<tr><th colSpan={2}>{t('PaymentHistory_PaymentMethodFee')}</th><td>{ui.formatAmount(initialValues.paymentMethodFee)}</td></tr>
									<tr><th colSpan={2}><div>{t('PaymentHistory_Discount')}</div><div style={{ fontWeight: 'normal', color: '#000' }}>{initialValues.discountText}</div></th><td>{ui.formatAmount(initialValues.discount)}</td></tr>
									<tr><th colSpan={2}>{t('PaymentHistory_Total')}</th><td>{ui.formatAmount(initialValues.total)}</td></tr>
									{initialValues.refundedTotal && Number(initialValues.refundedTotal) > 0 ? <tr>
										<th>{t('PaymentHistory_TotalRefunded')}</th>
										<td>{Number(initialValues.refundedQuantity) > 0 ? initialValues.refundedQuantity : ''}</td>
										<td>{ui.formatAmount(initialValues.refundedTotal)}</td>
									</tr> : null}
									{initialValues.isInstalmentPayment ? <tr>
										<th colSpan={2}><LabelWithTooltip id='paidTotalTooltip' tooltip={t('PaymentHistory_PaidTotalTooltip')}>{t('PaymentHistory_PaidTotal')} <FontAwesomeIcon icon={faCircleInfo} className='text-muted' style={{ fontSize: '14px', marginLeft: '4px' }} /></LabelWithTooltip></th>
										<td>{ui.formatAmount(initialValues.paidTotal)}</td>
									</tr> : null}
									{initialValues.isInstalmentPayment ? <tr>
										<th colSpan={2}>{t('PaymentHistory_OutstandingTotal')}</th>
										<td>{ui.formatAmount(initialValues.outstandingTotal)}</td>
									</tr> : null}
								</tfoot>
							</Table>
						</Panel.Body>
					</Panel>
				</Col>
				<Col sm={6}>
					<Panel>
						<Panel.Heading>{t('PaymentHistory_Notes')}</Panel.Heading>
						<Panel.Body>
							<Field name='notes' component={Static} labelSize={0} formatType='html' horizontal />
						</Panel.Body>
					</Panel>
				</Col>
			</Row>
			{initialValues.isInstalmentPayment && <InstalmentList instalments={initialValues.instalments.sort((a, b) => moment(a.chargeDate) - moment(b.chargeDate))} instalmentTemplateName={initialValues.instalmentTemplateName} instalmentPlan={initialValues.instalmentPlan} isInactive={isInactive}
				canRetryInstalment={canRetryInstalment} retryInstalment={retryInstalment} paymentMethod={initialValues.paymentMethod} onRefresh={onRefresh} markInstalmentAsPaid={markInstalmentAsPaid} canMarkInstalmentAsPaid={canMarkInstalmentAsPaid}/>}
			{canUploadInvoice && <Field name='invoiceId' component={FormFileUpload} disabled={isReadonly} horizontal label={t('PaymentHistory_Invoice')} initialData={invoiceFile} />}

			{!canUploadInvoice && <FormGroup>
				<Col sm={10} smOffset={2}>
					<Field name='invoice.url' component={renderInvoiceUrlField} text={t('PaymentHistory_ViewInvoice')} />
				</Col>
			</FormGroup>}

			<FormActions horizontal>
				<BackButton />
				<Button type='submit' disabled={isReadonly || pristine || submitting} bsStyle='primary'>{t('Common_Save')}</Button>
				<div className='pull-right'>
					{canMarkAsPaid && <Button onClick={markAsPaid} bsStyle='primary' style={{ marginRight: '5px' }}><FontAwesomeIcon icon={faCheck} /> {t('PaymentHistory_MarkAsPaid')}</Button>}
					{canCancel && <Button onClick={markAsCancelled} bsStyle='danger' style={{ marginRight: '5px' }}><FontAwesomeIcon icon={faTimes} /> {t('PaymentHistory_MarkAsCancelled')}</Button>}
					{canRefund && !isInactive && <ConfirmationButton
						onConfirmation={refundIndividualConsultation}
						confirmationTitle={t('IndividualConsultation_RefundConfirmationTitle')}
						confirmationMessage={t('IndividualConsultation_RefundConfirmationMessage')}
						displayType={'inline'}
						btnClasses='ml-05'
						label={t('IndividualConsultation_Refund')} />
					}
					{canSendInvoice && <ConfirmationButton
						onConfirmation={sendInvoice}
						confirmationTitle={t('PaymentHistory_SendInvoiceTitle')}
						confirmationMessage={t('PaymentHistory_SendInvoiceConfirmationMessage')}
						displayType={'inline'}
						bsStyle='warning'
						icon={faEnvelope}
						btnClasses='ml-05'
						label={t('PaymentHistory_SendInvoice')} />
					}
				</div>
			</FormActions>
		</Form>
	);
};

const renderInvoiceUrlField = (field) => (<span>
	{field.input.value && <a target='blank' href={field.input.value}>{field.text}</a>}
</span>);

export default reduxForm({ form: 'individualConsultation', enableReinitialize: true })(withTranslation()(SettingsForm));
